// @tennis-matches/services/governance/role-app-access

import { getSupabaseClient } from "../supabase/index.js";
import {
  isMissingRestResourceError,
  isRestEndpointUnavailable,
  markRestEndpointUnavailable,
} from "../supabase/rest-endpoint-availability.js";
import { isGlobalSuperadminForSession } from "./global-superadmin-session.js";
import { checkIsGlobalSuperadminRemote } from "./superadmin-check.service.js";
import { rejectInvalidTenantScopeId } from "./scopeIdValidation.js";
import { invalidateGovernanceBundleCache } from "./governance-bundle.service.js";

/** Align DB/RPC keys with governance catalog keys (clubchamp → clubmeisterschaft, etc.). */
function __normalizeAppKey(appKey) {
  const k = __asStr(appKey).toLowerCase();
  if (k === "schleifchen" || k === "schleifchenturnier") return "matchmixer";
  if (k === "clubchamp") return "clubmeisterschaft";
  if (k === "teamdesk") return "medenspiele";
  return k;
}

/** Kanonischer UI-Key → DB-Schlüssel in `app_config.role_app_access` / RPC-Allowlist. */
function __appKeyForRoleAppAccessRpc(appKey) {
  const k = __normalizeAppKey(appKey);
  if (k === "matchmixer") return "schleifchen";
  return k;
}

function __asStr(v) {
  return String(v ?? "").trim();
}

function __errorInfo(error) {
  if (!error) return null;
  return {
    message: __asStr(error.message || ""),
    code: __asStr(error.code || ""),
    details: __asStr(error.details || ""),
    hint: __asStr(error.hint || ""),
    status: error.status ?? null,
  };
}

/** PostgREST 404 / „function not found“ — weiterarbeiten mit Bindings/Entitlements ohne Spam */
function __isMissingPublicRpcError(error) {
  return isMissingRestResourceError(error);
}

/** Erwartete Ablehnung (z. B. global/global ohne Superadmin) → leere Matrix, kein Fehler. */
function __isRoleAppAccessSoftDenyError(error) {
  if (!error) return false;
  const status = Number(error?.status ?? error?.statusCode ?? 0);
  if (status !== 400 && status !== 401 && status !== 403) return false;
  const msg = __asStr(error?.message || error?.details || "").toLowerCase();
  return (
    msg.includes("not-authorized") ||
    msg.includes("not-authenticated") ||
    msg.includes("invalid-scope") ||
    msg.includes("missing-scope-id")
  );
}

function __roleAppAccessRestKey(scope, scopeId) {
  return `rpc:list_role_app_access:${__asStr(scope).toLowerCase()}:${__asStr(scopeId)}`;
}

/** Global-Matrix nur für globalen Superadmin — sonst 400 not-authorized ohne Nutzen. */
export function shouldLoadGlobalRoleAppAccessRemote(roleBindings, authUser) {
  return isGlobalSuperadminForSession(roleBindings, authUser);
}

async function __loadGlobalRoleAppAccessIfSuperadmin(roleBindings, authUser) {
  if (roleBindings != null || authUser != null) {
    if (!shouldLoadGlobalRoleAppAccessRemote(roleBindings, authUser)) {
      return { ok: true, rows: [], skippedScope: true };
    }
    return loadRoleAppAccessRemote("global", "global");
  }
  const superRes = await checkIsGlobalSuperadminRemote();
  if (superRes?.isSuperadmin !== true) {
    return { ok: true, rows: [], skippedScope: true };
  }
  return loadRoleAppAccessRemote("global", "global");
}

let __warnedMissingRoleAppAccessRpc = false;

/** @type {Map<string, Promise<{ ok: boolean, rows?: any[], reason?: string }>>} */
const __loadRoleAccessInflight = new Map();
/** @type {Map<string, { at: number, payload: { ok: boolean, rows: any[], fallbackMissingRpc?: boolean } }>} */
const __loadRoleAccessCache = new Map();
const ROLE_ACCESS_CACHE_TTL_MS = 30 * 1000;

function __cacheKey(scope, scopeId) {
  return `${__asStr(scope)}:::${__asStr(scopeId)}`;
}

function __mergedCacheKey(tenantScopeId) {
  return `merged:::${__asStr(tenantScopeId)}`;
}

function __purgeMergedRoleAccessCache() {
  for (const key of __loadRoleAccessCache.keys()) {
    if (key.startsWith("merged:::")) __loadRoleAccessCache.delete(key);
  }
  for (const key of __loadRoleAccessInflight.keys()) {
    if (key.startsWith("merged:::")) __loadRoleAccessInflight.delete(key);
  }
}

/**
 * Client-Fallback: globale Defaults + Tenant-Overrides für die Admin-Matrix.
 * @returns {Array<{ scope: string, scope_id: string, role: string, app_key: string, enabled: boolean, is_local_override: boolean, global_enabled: boolean }>}
 */
export function mergeRoleAppAccessRowsForTenant(tenantScopeId, tenantRows, globalRows) {
  const sid = __asStr(tenantScopeId);
  const globalMap = new Map();
  for (const r of Array.isArray(globalRows) ? globalRows : []) {
    const role = __asStr(r?.role).toLowerCase();
    const app = __normalizeAppKey(r?.app_key);
    if (!role || !app) continue;
    globalMap.set(`${role}|${app}`, r?.enabled !== false);
  }
  const tenantExplicit = new Set();
  const out = [];
  for (const r of Array.isArray(tenantRows) ? tenantRows : []) {
    const role = __asStr(r?.role).toLowerCase();
    const app = __normalizeAppKey(r?.app_key);
    if (!role || !app) continue;
    const k = `${role}|${app}`;
    tenantExplicit.add(k);
    out.push({
      scope: "tenant",
      scope_id: sid,
      role,
      app_key: app,
      enabled: r?.enabled !== false,
      is_local_override: true,
      global_enabled: globalMap.get(k) === true,
    });
  }
  for (const [k, globalEnabled] of globalMap.entries()) {
    if (tenantExplicit.has(k)) continue;
    const [role, app] = k.split("|");
    out.push({
      scope: "tenant",
      scope_id: sid,
      role,
      app_key: app,
      enabled: globalEnabled,
      is_local_override: false,
      global_enabled: globalEnabled,
    });
  }
  return out;
}

export async function loadRoleAppAccessMergedRemote(tenantScopeId) {
  const sid = __asStr(tenantScopeId);
  if (!sid) return { ok: false, reason: "missing-scope" };

  const key = __mergedCacheKey(sid);
  const now = Date.now();
  const hit = __loadRoleAccessCache.get(key);
  if (hit && now - hit.at < ROLE_ACCESS_CACHE_TTL_MS) {
    return hit.payload;
  }

  const pending = __loadRoleAccessInflight.get(key);
  if (pending) return pending;

  const run = (async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return { ok: false, reason: "no-supabase" };
      const { data, error } = await supabase.rpc("list_role_app_access_merged", {
        p_tenant_scope_id: sid,
      });

      if (!error && Array.isArray(data)) {
        const rows = data.map((r) => ({
          scope: "tenant",
          scope_id: sid,
          role: __asStr(r?.role).toLowerCase(),
          app_key: __normalizeAppKey(r?.app_key),
          enabled: r?.enabled !== false,
          is_local_override: Boolean(r?.is_local_override),
          global_enabled: r?.global_enabled === true,
        }));
        const okPayload = { ok: true, rows, merged: true };
        __loadRoleAccessCache.set(key, { at: Date.now(), payload: okPayload });
        return okPayload;
      }

      if (error && !__isMissingPublicRpcError(error)) {
        return { ok: false, reason: "rpc-error", errorInfo: __errorInfo(error), error };
      }

      const tenantRes = await loadRoleAppAccessRemote("tenant", sid);
      const globalRes = await __loadGlobalRoleAppAccessIfSuperadmin(null, null);
      if (!tenantRes?.ok && tenantRes?.reason !== "missing-scope") {
        return tenantRes || { ok: false, reason: "tenant-load-failed" };
      }
      const tenantList = tenantRes?.ok && Array.isArray(tenantRes.rows) ? tenantRes.rows : [];
      const globalList = globalRes?.ok && Array.isArray(globalRes.rows) ? globalRes.rows : [];
      const rows = mergeRoleAppAccessRowsForTenant(sid, tenantList, globalList);
      const okPayload = { ok: true, rows, merged: true, fallbackClientMerge: true };
      __loadRoleAccessCache.set(key, { at: Date.now(), payload: okPayload });
      return okPayload;
    } catch (e) {
      return { ok: false, reason: "exception", errorInfo: __errorInfo(e), error: e };
    } finally {
      __loadRoleAccessInflight.delete(key);
    }
  })();

  __loadRoleAccessInflight.set(key, run);
  return run;
}

export async function loadRoleAppAccessRemote(scope, scopeId) {
  const s = __asStr(scope);
  const sid = __asStr(scopeId);
  if (!s || !sid) return { ok: false, reason: "missing-scope" };

  const key = __cacheKey(s, sid);
  const now = Date.now();
  const hit = __loadRoleAccessCache.get(key);
  if (hit && now - hit.at < ROLE_ACCESS_CACHE_TTL_MS) {
    return hit.payload;
  }

  const pending = __loadRoleAccessInflight.get(key);
  if (pending) return pending;

  const restKey = __roleAppAccessRestKey(s, sid);
  if (isRestEndpointUnavailable(restKey)) {
    const cachedMissing = { ok: true, rows: [], fallbackMissingRpc: true };
    __loadRoleAccessCache.set(key, { at: Date.now(), payload: cachedMissing });
    return cachedMissing;
  }

  const run = (async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return { ok: false, reason: "no-supabase" };
      const { data, error } = await supabase.rpc("list_role_app_access", {
        p_scope: s,
        p_scope_id: sid,
      });

      if (error) {
        if (__isMissingPublicRpcError(error) || __isRoleAppAccessSoftDenyError(error)) {
          markRestEndpointUnavailable(restKey);
          if (__isMissingPublicRpcError(error)) {
            if (!__warnedMissingRoleAppAccessRpc) {
              __warnedMissingRoleAppAccessRpc = true;
              console.warn(
                "[governance] list_role_app_access nicht erreichbar (404/fehlende public-RPC). Fallback: leere role_app_access-Zeilen; nutze role_bindings/app_entitlements.",
                __errorInfo(error)
              );
            }
          }
          const fallbackPayload = {
            ok: true,
            rows: [],
            fallbackMissingRpc: __isMissingPublicRpcError(error),
            fallbackDenied: __isRoleAppAccessSoftDenyError(error),
          };
          __loadRoleAccessCache.set(key, { at: Date.now(), payload: fallbackPayload });
          return fallbackPayload;
        }
        return { ok: false, reason: "rpc-error", errorInfo: __errorInfo(error), error };
      }

      const okPayload = { ok: true, rows: Array.isArray(data) ? data : [] };
      __loadRoleAccessCache.set(key, { at: Date.now(), payload: okPayload });
      return okPayload;
    } catch (e) {
      return { ok: false, reason: "exception", errorInfo: __errorInfo(e), error: e };
    } finally {
      __loadRoleAccessInflight.delete(key);
    }
  })();

  __loadRoleAccessInflight.set(key, run);
  return run;
}

export async function setRoleAppAccessRemote({ scope, scopeId, role, appKey, enabled }) {
  const s = __asStr(scope);
  const sid = __asStr(scopeId);
  const r = __asStr(role);
  const app = __asStr(appKey);
  if (!s || !sid || !r || !app) return { ok: false, reason: "missing-fields" };
  const scopeErr = rejectInvalidTenantScopeId(s, sid);
  if (scopeErr) return scopeErr;
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };
    const { data, error } = await supabase.rpc("set_role_app_access", {
      p_scope: s,
      p_scope_id: sid,
      p_role: r,
      p_app_key: __appKeyForRoleAppAccessRpc(app),
      p_enabled: Boolean(enabled),
    });
    if (error) {
      if (__isMissingPublicRpcError(error) && !__warnedMissingRoleAppAccessRpc) {
        __warnedMissingRoleAppAccessRpc = true;
        console.warn(
          "[governance] set_role_app_access nicht erreichbar (404/fehlende public-RPC). Admin-Schreibzugriff auf role_app_access ausstehend.",
          __errorInfo(error)
        );
      }
      return { ok: false, reason: "rpc-error", errorInfo: __errorInfo(error), error };
    }
    __loadRoleAccessCache.delete(__cacheKey(s, sid));
    if (s === "tenant") {
      __loadRoleAccessCache.delete(__mergedCacheKey(sid));
    } else if (s === "global") {
      __purgeMergedRoleAccessCache();
    }
    invalidateGovernanceBundleCache();
    return { ok: true, result: data || null };
  } catch (e) {
    return { ok: false, reason: "exception", errorInfo: __errorInfo(e), error: e };
  }
}

